import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, MessageCircle, Users, Building, Phone, Info, RefreshCw, StopCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppTemplateSend = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ sent: 0, failed: 0, total: 0 });
  const stopSendingRef = useRef(false);
  
  // WhatsApp numbers for each magazine
  const WHATSAPP_NUMBERS = {
    'Aabkari Times': '+1 762 770 0003',
    'Sugar Times': '+1 551 365 3462'
  };
  
  const [formData, setFormData] = useState({
    template_sid: localStorage.getItem('wa_template_sid') || '',
    magazine: 'Aabkari Times',
    target_audience: 'members_active',
    edition_name: '',
    magazine_link: '',
    custom_numbers: ''
  });

  // Persist template_sid in localStorage
  useEffect(() => {
    if (formData.template_sid) {
      localStorage.setItem('wa_template_sid', formData.template_sid);
    }
  }, [formData.template_sid]);

  const handleMigrateWhatsAppNumbers = async () => {
    if (!window.confirm('This will copy the first contact number to WhatsApp number field for all members and advertisers. Continue?')) {
      return;
    }
    
    setMigrateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/migrate-whatsapp-numbers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Migration complete: ${response.data.members_updated} members, ${response.data.advertisers_updated} advertisers updated`);
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrateLoading(false);
    }
  };

  // Get recipient count before sending
  const handlePreviewRecipients = async () => {
    if (!formData.template_sid) {
      toast.error('Please enter the Template Content SID');
      return;
    }
    
    if (formData.target_audience === 'custom' && !formData.custom_numbers.trim()) {
      toast.error('Please enter at least one phone number');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get recipient count
      const customNumbers = formData.target_audience === 'custom' 
        ? formData.custom_numbers.split(/[\n,;]/).map(n => n.trim()).filter(n => n)
        : [];
      
      const response = await axios.post(`${API}/whatsapp/preview-recipients`, {
        magazine: formData.magazine,
        target_audience: formData.target_audience,
        custom_numbers: customNumbers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRecipientCount(response.data.count);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get recipient count');
    } finally {
      setLoading(false);
    }
  };

  const handleStopSending = () => {
    stopSendingRef.current = true;
    toast.warning('Stopping... Will complete after current batch');
  };

  const handleConfirmSend = async () => {
    setShowConfirmation(false);
    stopSendingRef.current = false;
    setLoading(true);
    setSendingProgress({ sent: 0, failed: 0, total: recipientCount });
    setResults(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const customNumbers = formData.target_audience === 'custom' 
        ? formData.custom_numbers.split(/[\n,;]/).map(n => n.trim()).filter(n => n)
        : [];
      
      // Only include variables that have values
      const variables = {};
      if (formData.edition_name) variables["1"] = formData.edition_name;
      if (formData.magazine_link) variables["2"] = formData.magazine_link;
      
      const payload = {
        template_sid: formData.template_sid,
        magazine: formData.magazine,
        target_audience: formData.target_audience,
        custom_numbers: customNumbers,
        variables: Object.keys(variables).length > 0 ? variables : {}
      };
      
      const response = await axios.post(`${API}/whatsapp/send-template`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSendingProgress({ 
        sent: response.data.success_count, 
        failed: response.data.failed_count, 
        total: response.data.success_count + response.data.failed_count 
      });
      setResults(response.data);
      
      if (stopSendingRef.current) {
        toast.warning(`Stopped! Sent to ${response.data.success_count} of ${recipientCount} recipients`);
      } else {
        toast.success(`Completed! Sent to ${response.data.success_count} recipients`);
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally {
      setLoading(false);
      stopSendingRef.current = false;
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-8">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="page-title">
              <MessageCircle className="inline mr-3" size={36} />
              WhatsApp Magazine Broadcast
            </h1>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <Info className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm text-blue-700">
                  <strong>How it works:</strong> Enter your pre-approved WhatsApp template SID from Twilio.
                  The template can have variables like <code className="bg-blue-100 px-1 rounded">{'{{1}}'}</code> and <code className="bg-blue-100 px-1 rounded">{'{{2}}'}</code>. Leave variable fields empty if your template has no variables.
                </p>
              </div>
            </div>
          </div>

          {/* WhatsApp Number Indicator */}
          <div className={`border p-4 rounded-lg mb-6 ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Phone className={`mr-3 ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-600' : 'text-green-600'}`} size={24} />
                <div>
                  <p className={`font-semibold ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-700' : 'text-green-700'}`}>
                    Sending from: {formData.magazine}
                  </p>
                  <p className={`text-sm ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-600' : 'text-green-600'}`}>
                    WhatsApp Number: <strong>{WHATSAPP_NUMBERS[formData.magazine]}</strong>
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'bg-purple-200 text-purple-700' : 'bg-green-200 text-green-700'}`}>
                {formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG' ? 'AT' : 'ST'}
              </div>
            </div>
          </div>

          {/* Migration Tool */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-800 font-semibold">One-time Migration</p>
                <p className="text-xs text-yellow-700">Copy contact numbers to WhatsApp number field for existing members/advertisers</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleMigrateWhatsAppNumbers} 
                disabled={migrateLoading}
                className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
              >
                <RefreshCw className={`mr-2 ${migrateLoading ? 'animate-spin' : ''}`} size={16} />
                {migrateLoading ? 'Migrating...' : 'Migrate Numbers'}
              </Button>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-6" style={{ color: '#059669' }}>
              Send Template Message
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template SID */}
              <div className="space-y-2 md:col-span-2">
                <Label>Template Content SID *</Label>
                <Input
                  value={formData.template_sid}
                  onChange={(e) => setFormData({ ...formData, template_sid: e.target.value })}
                  placeholder="e.g., HXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  data-testid="template-sid-input"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">Get this from Twilio Console → Messaging → Content Editor</p>
              </div>

              {/* Magazine */}
              <div className="space-y-2">
                <Label>Magazine *</Label>
                <Select 
                  value={formData.magazine} 
                  onValueChange={(v) => setFormData({ ...formData, magazine: v })}
                  disabled={loading}
                >
                  <SelectTrigger data-testid="magazine-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Target Audience */}
              <div className="space-y-2">
                <Label>Send To *</Label>
                <Select 
                  value={formData.target_audience} 
                  onValueChange={(v) => setFormData({ ...formData, target_audience: v })}
                  disabled={loading}
                >
                  <SelectTrigger data-testid="audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="members_active">
                      <div className="flex items-center">
                        <Users size={16} className="mr-2 text-green-600" />
                        Members - Active Only
                      </div>
                    </SelectItem>
                    <SelectItem value="members_inactive">
                      <div className="flex items-center">
                        <Users size={16} className="mr-2 text-red-600" />
                        Members - Inactive Only
                      </div>
                    </SelectItem>
                    <SelectItem value="members">
                      <div className="flex items-center">
                        <Users size={16} className="mr-2" />
                        Members - All
                      </div>
                    </SelectItem>
                    <SelectItem value="advertisers_active">
                      <div className="flex items-center">
                        <Building size={16} className="mr-2 text-green-600" />
                        Advertisers - Active Only
                      </div>
                    </SelectItem>
                    <SelectItem value="advertisers_inactive">
                      <div className="flex items-center">
                        <Building size={16} className="mr-2 text-red-600" />
                        Advertisers - Inactive Only
                      </div>
                    </SelectItem>
                    <SelectItem value="advertisers">
                      <div className="flex items-center">
                        <Building size={16} className="mr-2" />
                        Advertisers - All
                      </div>
                    </SelectItem>
                    <SelectItem value="both">
                      <div className="flex items-center">
                        <Users size={16} className="mr-2" />
                        All Members + Advertisers
                      </div>
                    </SelectItem>
                    <SelectItem value="custom">
                      <div className="flex items-center">
                        <Phone size={16} className="mr-2" />
                        Custom Numbers
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Edition Name - Variable 1 */}
              <div className="space-y-2">
                <Label>Edition Name (Variable 1)</Label>
                <Input
                  value={formData.edition_name}
                  onChange={(e) => setFormData({ ...formData, edition_name: e.target.value })}
                  placeholder="e.g., January 2026"
                  data-testid="edition-name-input"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">This will replace {'{{1}}'} in your template</p>
              </div>

              {/* Magazine Link - Variable 2 */}
              <div className="space-y-2">
                <Label>Magazine Link (Variable 2)</Label>
                <Input
                  value={formData.magazine_link}
                  onChange={(e) => setFormData({ ...formData, magazine_link: e.target.value })}
                  placeholder="e.g., /january2026 or https://..."
                  data-testid="magazine-link-input"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">This will replace {'{{2}}'} in your template</p>
              </div>

              {/* Custom Numbers */}
              {formData.target_audience === 'custom' && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Phone Numbers (one per line or comma-separated)</Label>
                  <Textarea
                    value={formData.custom_numbers}
                    onChange={(e) => setFormData({ ...formData, custom_numbers: e.target.value })}
                    placeholder="9876543210&#10;9123456789&#10;..."
                    rows={5}
                    data-testid="custom-numbers-input"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">Enter 10-digit numbers without +91</p>
                </div>
              )}
            </div>

            {/* Progress Bar during sending */}
            {loading && sendingProgress.total > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-blue-700">
                    Sending in progress...
                  </span>
                  <span className="text-sm text-blue-600">
                    {sendingProgress.sent + sendingProgress.failed} / {sendingProgress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${((sendingProgress.sent + sendingProgress.failed) / sendingProgress.total) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs">
                  <span className="text-green-600">✓ Sent: {sendingProgress.sent}</span>
                  <span className="text-red-600">✗ Failed: {sendingProgress.failed}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex gap-4">
              {!loading ? (
                <Button 
                  onClick={handlePreviewRecipients} 
                  disabled={loading}
                  className="flex-1 py-6 text-lg"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="send-button"
                >
                  <Send className="mr-2" size={20} />
                  Preview & Send
                </Button>
              ) : (
                <Button 
                  onClick={handleStopSending}
                  variant="destructive"
                  className="flex-1 py-6 text-lg"
                  data-testid="stop-button"
                >
                  <StopCircle className="mr-2" size={20} />
                  STOP SENDING
                </Button>
              )}
            </div>
          </div>

          {/* Confirmation Modal */}
          {showConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="text-yellow-500 mr-3" size={32} />
                  <h2 className="text-xl font-bold">Confirm Send</h2>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                  <p className="text-lg text-center">
                    You are about to send WhatsApp messages to
                  </p>
                  <p className="text-4xl font-bold text-center text-green-600 my-2">
                    {recipientCount} recipients
                  </p>
                  <p className="text-sm text-center text-gray-600">
                    Template: {formData.edition_name}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
                  <p><strong>Magazine:</strong> {formData.magazine}</p>
                  <p><strong>Audience:</strong> {formData.target_audience}</p>
                  <p><strong>Variable 1:</strong> {formData.edition_name}</p>
                  <p><strong>Variable 2:</strong> {formData.magazine_link}</p>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  You can click <strong>STOP SENDING</strong> button anytime to cancel the operation.
                </p>

                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowConfirmation(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleConfirmSend}
                    className="flex-1"
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Send className="mr-2" size={16} />
                    Yes, Send Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#059669' }}>
                Broadcast Results
              </h2>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold">{results.success_count + results.failed_count}</p>
                  <p className="text-sm text-gray-600">Total Recipients</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{results.success_count}</p>
                  <p className="text-sm text-green-700">Sent Successfully</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{results.failed_count}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>

              {results.results && results.results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-3 py-2 text-left">ID</th>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Phone</th>
                        <th className="border px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.slice(0, 20).map((r, idx) => (
                        <tr key={idx} className={r.status === 'failed' ? 'bg-red-50' : ''}>
                          <td className="border px-3 py-2">{r.id}</td>
                          <td className="border px-3 py-2">{r.name}</td>
                          <td className="border px-3 py-2">{r.phone}</td>
                          <td className="border px-3 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              r.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {r.status === 'sent' ? '✅ Sent' : '❌ Failed'}
                            </span>
                            {r.status === 'failed' && r.error && (
                              <p className="text-xs text-red-600 mt-1 text-left">{r.error}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {results.results.length > 20 && (
                    <p className="text-sm text-gray-500 mt-2">Showing first 20 of {results.results.length} results</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WhatsAppTemplateSend;
